import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Play } from "lucide-react";
import axios from "axios";
import { resolveImageUrl } from "../utils/image";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/portfolio/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      setError("Project not found");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-40 flex items-center justify-center">
        <div className="text-[#A3A3A3]">Loading...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#050505] pt-40 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-[#EDEDED] mb-4">Project Not Found</h2>
        <p className="text-[#A3A3A3] mb-8">The project you're looking for doesn't exist.</p>
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 text-[#D4C5B0] hover:gap-4 transition-all"
        >
          <ArrowLeft size={18} />
          Back to Portfolio
        </Link>
      </div>
    );
  }

  const kind = project.media_type === "video" ? "Videography" : "Photography";
  const seoDesc = project.seo_description || (project.description || `${project.title} – ${kind.toLowerCase()} project by OBRA Creatives, photographer & videographer in Manawatū and Wellington.`).slice(0, 160);
  const coverUrl = resolveImageUrl(project.image_url);
  const projectSchema = {
    "@context": "https://schema.org",
    "@type": project.media_type === "video" ? "VideoObject" : "ImageGallery",
    "name": project.title,
    "description": seoDesc,
    ...(project.media_type === "video" ? { "thumbnailUrl": coverUrl, "uploadDate": project.created_at, ...(project.video_url ? { "embedUrl": resolveImageUrl(project.video_url) } : {}) } : { "image": (project.images?.length ? project.images : [project.image_url]).map(resolveImageUrl) }),
    "creator": { "@type": "Organization", "name": "OBRA Creatives", "url": "https://www.obracreatives.co.nz" }
  };

  return (
    <div data-testid="project-detail-page" className="pt-40 bg-[#050505]">
      <SEO title={project.seo_title || `${project.title} – ${kind}`} description={seoDesc}
        path={`/portfolio/${project.id}`} image={coverUrl} type="article" schema={projectSchema} />
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link
          to="/portfolio"
          data-testid="back-to-portfolio"
          className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#EDEDED] transition-colors"
        >
          <ArrowLeft size={18} />
          Back to Portfolio
        </Link>
      </div>

      {/* Hero Image / Video */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="aspect-[16/9] overflow-hidden relative"
          >
            {project.media_type === "video" && project.video_url ? (
              videoPlaying ? (
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(project.video_url)}?rel=0&modestbranding=1&autoplay=1`}
                  title={project.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  data-testid="video-thumbnail-overlay"
                  className="relative w-full h-full cursor-pointer group"
                  onClick={() => setVideoPlaying(true)}
                >
                  <img
                    src={resolveImageUrl(project.image_url)}
                    alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-[#D4C5B0] rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                      <Play size={32} className="text-[#050505] ml-1" fill="#050505" />
                    </div>
                  </div>
                </div>
              )
            ) : (
              <img
                src={resolveImageUrl(project.image_url)}
                alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* Album Gallery (if multiple images) */}
      {project.images && project.images.length > 1 && (
        <section className="pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {project.images.map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="aspect-square overflow-hidden bg-[#121212] cursor-pointer group"
                  onClick={() => {
                    const resolved = resolveImageUrl(img);
                    const lb = document.createElement('div');
                    lb.className = 'fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-pointer';
                    lb.onclick = () => lb.remove();
                    lb.innerHTML = `<img src="${resolved}" alt="" class="max-w-full max-h-full object-contain" />`;
                    document.body.appendChild(lb);
                  }}
                >
                  <img src={resolveImageUrl(img)} alt={`${project.title} – photo ${i + 1} by OBRA Creatives, Manawatū & Wellington photographer`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project Info */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                  {project.category}
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EDEDED] mt-4 mb-6">
                  {project.title}
                </h1>
                <p className="text-[#A3A3A3] text-lg leading-relaxed">
                  {project.description}
                </p>
              </motion.div>

              {/* Case Study */}
              {(project.problem || project.approach || project.outcome) && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mt-16 space-y-12"
                >
                  {project.problem && (
                    <div data-testid="case-study-problem">
                      <h2 className="text-xl font-bold text-[#EDEDED] mb-4 flex items-center gap-3">
                        <span className="w-8 h-px bg-[#D4C5B0]" />
                        The Challenge
                      </h2>
                      <p className="text-[#A3A3A3] leading-relaxed pl-11">
                        {project.problem}
                      </p>
                    </div>
                  )}

                  {project.approach && (
                    <div data-testid="case-study-approach">
                      <h2 className="text-xl font-bold text-[#EDEDED] mb-4 flex items-center gap-3">
                        <span className="w-8 h-px bg-[#D4C5B0]" />
                        Our Approach
                      </h2>
                      <p className="text-[#A3A3A3] leading-relaxed pl-11">
                        {project.approach}
                      </p>
                    </div>
                  )}

                  {project.outcome && (
                    <div data-testid="case-study-outcome">
                      <h2 className="text-xl font-bold text-[#EDEDED] mb-4 flex items-center gap-3">
                        <span className="w-8 h-px bg-[#D4C5B0]" />
                        The Outcome
                      </h2>
                      <p className="text-[#A3A3A3] leading-relaxed pl-11">
                        {project.outcome}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#121212] border border-[#262626] p-8 sticky top-32">
                <h3 className="text-lg font-bold text-[#EDEDED] mb-6">
                  Project Details
                </h3>
                
                <div className="space-y-6">
                  {project.client && (
                    <div>
                      <span className="text-[#A3A3A3] text-sm uppercase tracking-wider">
                        Client
                      </span>
                      <p className="text-[#EDEDED] mt-1">{project.client}</p>
                    </div>
                  )}
                  
                  <div>
                    <span className="text-[#A3A3A3] text-sm uppercase tracking-wider">
                      Category
                    </span>
                    <p className="text-[#EDEDED] mt-1">{project.category}</p>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-[#262626]">
                  <Link
                    to="/contact"
                    data-testid="project-cta-button"
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
                  >
                    Start Similar Project
                    <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center">
            <Link
              to="/portfolio"
              data-testid="view-all-projects"
              className="inline-flex items-center gap-2 text-[#D4C5B0] font-medium hover:gap-4 transition-all"
            >
              <ArrowLeft size={18} />
              View All Projects
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[#D4C5B0] font-medium hover:gap-4 transition-all"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
